import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const TicketCartContext = createContext();

export function TicketCartProvider({ children }) {
  // Cart items: { event_id, ticket_type_id, event_title, ticket_type_name, price, quantity }
  const [cartItems, setCartItems] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('ticketCart');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [cartOpen, setCartOpen] = useState(false);
  const [validatedCart, setValidatedCart] = useState(null);
  const [validating, setValidating] = useState(false);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('ticketCart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = useCallback((item) => {
    setCartItems(prev => {
      const existingIndex = prev.findIndex(
        i => i.event_id === item.event_id && i.ticket_type_id === item.ticket_type_id
      );
      
      if (existingIndex >= 0) {
        // Update quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + item.quantity
        };
        return updated;
      }
      
      // Add new item
      return [...prev, item];
    });
    setValidatedCart(null); // Invalidate cached validation
  }, []);

  const updateQuantity = useCallback((eventId, ticketTypeId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(eventId, ticketTypeId);
      return;
    }
    
    setCartItems(prev => 
      prev.map(item => 
        item.event_id === eventId && item.ticket_type_id === ticketTypeId
          ? { ...item, quantity }
          : item
      )
    );
    setValidatedCart(null);
  }, []);

  const removeFromCart = useCallback((eventId, ticketTypeId) => {
    setCartItems(prev => 
      prev.filter(item => 
        !(item.event_id === eventId && item.ticket_type_id === ticketTypeId)
      )
    );
    setValidatedCart(null);
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setValidatedCart(null);
    localStorage.removeItem('ticketCart');
  }, []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [cartItems]);

  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  const toggleCart = useCallback(() => {
    setCartOpen(prev => !prev);
  }, []);

  const value = {
    cartItems,
    cartOpen,
    setCartOpen,
    validatedCart,
    setValidatedCart,
    validating,
    setValidating,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartItemCount,
    toggleCart
  };

  return (
    <TicketCartContext.Provider value={value}>
      {children}
    </TicketCartContext.Provider>
  );
}

export function useTicketCart() {
  const context = useContext(TicketCartContext);
  if (!context) {
    throw new Error('useTicketCart must be used within a TicketCartProvider');
  }
  return context;
}

export default TicketCartContext;
